import React from "react";
import PropTypes from "prop-types";
import { capitalizarTexto } from "../../../shared/utils/formatador";
import { calcularIdade } from "../../../shared/utils/calcularIdade";

const TabUltimosExames = ({ paciente, dadosCompletos }) => {
  const statusColo = dadosCompletos?.statusColo || "—";
  const statusGeralMama = dadosCompletos?.statusGeralMama || "—";
  const diretrizMama = dadosCompletos?.diretrizMama || "—";
  const diretriz = dadosCompletos?.diretriz || "—";
  const tipoRastreamento = dadosCompletos?.tipoControle || "—";
  const statusAlertaRastreamento =
    dadosCompletos?.statusAlertaRastreamento || "—";
  const statusAlertaRastreamentoMama =
    dadosCompletos?.statusAlertaRastreamentoMama || "—";
  const dataNascimento = dadosCompletos?.dataNascimento || "Sem informação";
  const dataValidadeDiretriz = dadosCompletos?.dataValidadeDiretriz || null;
  const dataValidadeDiretrizMama =
    dadosCompletos?.dataValidadeDiretrizMama || null;

  const formatarData = (dataISO) => {
    if (!dataISO) return "—";

    const partes = dataISO.split("T")[0].split("-");

    if (partes.length !== 3) {
      return "—";
    }

    const [ano, mes, dia] = partes;

    if (!ano || !mes || !dia) {
      return "—";
    }

    return `${dia}/${mes}/${ano}`;
  };

  // Exames de colo
  const examesColoSiscolo = dadosCompletos?.resultadoExameCitColoSiscolo || [];
  const examesHistoColo = (dadosCompletos?.resultadoExameHistcolo || []).map(
    (exame) => ({
      dataLiberacao: exame.dataLiberacao,
      dataExame: exame.dataExame,
      examResultTypeDescription: exame.tipoExame || "Histopatológico de Colo",
      resultado: exame.resultado,
    }),
  );

  const resultadoExameColposcopia = (
    dadosCompletos?.resultadoExameColposcopia || []
  ).map((exame) => ({
    dataExame: exame.dataExame,
    dataLiberacao: exame.dataLiberacao,
    resultado: exame.resultado,
    examResultTypeDescription: exame.tipoExame,
  }));

  const examesColo = [
    ...examesColoSiscolo,
    ...examesHistoColo,
    ...resultadoExameColposcopia,
  ];

  const examesMamografia = (
    dadosCompletos?.resultadoExameMamografiaSismama || []
  ).map((exame) => ({
    dataExame: exame.dataExame,
    dataLiberacao: exame.dataLiberacao,
    examResultTypeDescription: exame.tipoExame || "Mamografia",
    resultado: exame.resultado || "—",
  }));

  const examesHistoMama = (dadosCompletos?.resultadoExameHistmama || []).map(
    (exame) => ({
      dataExame: exame.dataExame,
      dataLiberacao: exame.dataLiberacao,
      examResultTypeDescription: exame.tipoExame || "Histopatológico de Mama",
      resultado: exame.resultado || "—",
    }),
  );

  const examesCitoMama = (
    dadosCompletos?.cytopathologicalExamResultMama || []
  ).map((exame) => ({
    dataExame: exame.dataExame,
    dataLiberacao: exame.dataLiberacao,
    examResultTypeDescription: exame.tipoExame || "Citopatológico de Mama",
    resultado: exame.resultado || "—",
  }));

  const resultadoExameUltrassonografia = (
    dadosCompletos?.resultadoExameUltrassonografia || []
  ).map((exame) => ({
    dataExame: exame.dataExame,
    dataLiberacao: exame.dataLiberacao,
    resultado: exame.resultado,
    examResultTypeDescription: exame.tipoExame,
  }));

  const examesMama = [
    ...examesMamografia,
    ...examesHistoMama,
    ...examesCitoMama,
    ...resultadoExameUltrassonografia,
  ];

  return (
    <div className="ultimos-exames">
      <div className="informacoes-pessoais p-3 d-grid gap-4">
        <div className="row g-3">
          <div className="col-12 col-md-3">
            <div className="text-muted small mb-1">Paciente</div>
            <div className="fw-semibold text-dark">
              {capitalizarTexto(paciente.nome) || "—"}
            </div>
          </div>

          <div className="col-6 col-md-2">
            <div className="text-muted small mb-1">ID</div>
            <div className="fw-semibold">{paciente.id || "—"}</div>
          </div>

          <div className="col-6 col-md-2">
            <div className="text-muted small mb-1">Data de nascimento</div>
            <div className="fw-semibold">{formatarData(dataNascimento)}</div>
          </div>

          <div className="col-6 col-md-2">
            <div className="text-muted small mb-1">Idade</div>
            <div className="fw-semibold">{calcularIdade(dataNascimento)}</div>
          </div>

          <div className="col-6 col-md-3">
            <div className="text-muted small mb-1">Tipo de Rastreamento</div>
            <div className="fw-semibold">{tipoRastreamento}</div>
          </div>
        </div>
      </div>

      <div className="exames p-3 d-grid gap-4">
        <div className="colo-do-utero">
          <div className="row g-2 mb-3">
            <div className="col-md-12">
              <h5>Exames</h5>
              <hr />
            </div>
            <h6>Colo do Útero</h6>
            <div className="col-12 col-md-6">
              <strong>Status</strong>
              <div className="fw-semibold">{capitalizarTexto(statusColo)}</div>
            </div>
            <div className="col-12 col-md-6">
              <strong>Diretriz</strong>
              <div className="fw-semibold">{capitalizarTexto(diretriz)}</div>
            </div>
            <div className="col-12 col-md-6">
              <strong>Prazo de Alerta</strong>
              <div className="fw-semibold">{statusAlertaRastreamento}</div>
            </div>
            <div className="col-12 col-md-6">
              <strong>Data do Alerta</strong>
              <div className="fw-semibold">
                {statusAlertaRastreamento === "Sem alerta"
                  ? "-"
                  : formatarData(dataValidadeDiretriz)}
              </div>
            </div>
          </div>
          <div className="row g-3">
            <div className="table-responsive">
              <table className="table table-sm table-bordered table-hover align-middle">
                <thead className="thead-light">
                  <tr>
                    <th>Data do Exame</th>
                    <th>Tipo de Exame</th>
                    <th>Data de Liberação</th>
                    <th>Resultado</th>
                  </tr>
                </thead>
                <tbody>
                  {examesColo.length > 0 ? (
                    [...examesColo]
                      .sort(
                        (a, b) => new Date(b.dataExame) - new Date(a.dataExame),
                      )
                      .map((exame, i) => (
                        <tr key={i}>
                          <td>{formatarData(exame.dataExame)}</td>
                          <td>{exame.examResultTypeDescription || "—"}</td>
                          <td>{formatarData(exame.dataLiberacao)}</td>
                          <td>{capitalizarTexto(exame.resultado) || "—"}</td>
                        </tr>
                      ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center">
                        Nenhum exame encontrado
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="mama">
          <h5 className="mb-3" translate="no">
            Mama
          </h5>
          <div className="row g-2 align-items-center mb-3">
            <div className="col-12 col-md-6">
              <strong>Status</strong>
              <div className="fw-semibold">
                {capitalizarTexto(statusGeralMama)}
              </div>
            </div>
            <div className="col-12 col-md-6">
              <strong>Diretriz</strong>
              <div className="fw-semibold">
                {capitalizarTexto(diretrizMama)}
              </div>
            </div>
            <div className="col-12 col-md-6">
              <strong>Prazo de Alerta</strong>
              <div className="fw-semibold">{statusAlertaRastreamentoMama}</div>
            </div>
            <div className="col-12 col-md-6">
              <strong>Data do Alerta</strong>
              <div className="fw-semibold">
                {!dataValidadeDiretrizMama
                  ? "-"
                  : formatarData(dataValidadeDiretrizMama)}
              </div>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-sm table-bordered table-hover align-middle">
              <thead className="thead-light">
                <tr>
                  <th scope="col">Data do Exame</th>
                  <th scope="col">Tipo de Exame</th>
                  <th scope="col">Data de Liberação</th>
                  <th scope="col">Resultado</th>
                </tr>
              </thead>
              <tbody>
                {examesMama.length > 0 ? (
                  [...examesMama]
                    .sort(
                      (a, b) => new Date(b.dataExame) - new Date(a.dataExame),
                    )
                    .map((exame, i) => (
                      <tr key={i}>
                        <td>{formatarData(exame.dataExame)}</td>
                        <td>{exame.examResultTypeDescription || "—"}</td>
                        <td>{formatarData(exame.dataLiberacao)}</td>
                        <td>{exame.resultado || "—"}</td>
                      </tr>
                    ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center">
                      Nenhum exame encontrado
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

TabUltimosExames.propTypes = {
  paciente: PropTypes.object.isRequired,
  dadosCompletos: PropTypes.object,
};

export default TabUltimosExames;
